/**
 * ChatwayWidget.jsx — the Smartsupp live chat, everywhere except the admin console.
 *
 * (File and component name kept as "ChatwayWidget" so the import in App.js
 * keeps working. Rename both if you like.)
 *
 * Rendered once in App.js inside <Router>. It watches the route:
 *   • any normal page  → loads the Smartsupp script (once) and shows the chat bubble
 *   • /admin/...       → hides the bubble and closes any open chat
 *
 * The script is only fetched on the first non-admin page, so an admin who goes
 * straight to /admin never loads it. Once loaded it can't be unloaded in a
 * single-page app, so on admin routes it is hidden instead (Smartsupp's own
 * chat:hide command, plus a CSS fallback).
 *
 * Pages must NOT add their own Smartsupp <script> any more — this is the only loader.
 * Remove the old Chatway <script> from index.html / pages too.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SMARTSUPP_KEY = '555309e6f03200ed5b1291c399e26542519478c8';
const SCRIPT_ID = 'smartsupp-loader';
const HIDE_CLASS = 'no-smartsupp';

const HIDE_CSS = `
html.${HIDE_CLASS} #chat-application,
html.${HIDE_CLASS} #smartsupp-widget-container,
html.${HIDE_CLASS} iframe#chat-application,
html.${HIDE_CLASS} iframe[id^="smartsupp"] { display: none !important; }
`;

export const isAdminPath = (path) => path === '/admin' || path.startsWith('/admin/');

// Safe wrapper: only talks to Smartsupp once its command queue/stub exists
function ss(...args) {
  try { window.smartsupp?.(...args); } catch { /* widget not ready yet */ }
}

function loadSmartsupp() {
  if (document.getElementById(SCRIPT_ID)) return;

  window._smartsupp = window._smartsupp || {};
  window._smartsupp.key = SMARTSUPP_KEY;

  // Command queue stub (same as the official snippet) so ss() calls made
  // before the script finishes loading are replayed afterwards.
  if (!window.smartsupp) {
    const o = function () { o._.push(arguments); };
    o._ = [];
    window.smartsupp = o;
  }

  const s = document.createElement('script');
  s.id = SCRIPT_ID;
  s.type = 'text/javascript';
  s.charset = 'utf-8';
  s.async = true;
  s.src = 'https://www.smartsuppchat.com/loader.js?';
  document.body.appendChild(s);
}

export default function SmartsuppWidget() {
  const { pathname } = useLocation();
  const admin = isAdminPath(pathname);

  // The hide rule lives in <head> for the life of the app
  useEffect(() => {
    if (document.getElementById('smartsupp-hide-css')) return;
    const st = document.createElement('style');
    st.id = 'smartsupp-hide-css';
    st.textContent = HIDE_CSS;
    document.head.appendChild(st);
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    if (admin) {
      root.classList.add(HIDE_CLASS);
      ss('chat:close');
      ss('chat:hide');
    } else {
      root.classList.remove(HIDE_CLASS);
      loadSmartsupp();
      ss('chat:show');
    }
  }, [admin]);

  return null;
}